<template>
  <div
    class="group relative flex h-full flex-col pt-16 md:pt-40"
    data-spec="product-card-content"
  >
    <div class="p-16 pt-0" data-spec="main">
      <div class="flex">
        <div
          class="flex max-w-full grow flex-wrap content-start justify-center gap-8"
          data-spec="image+main-information"
        >
          <div
            class="flex flex-col items-center justify-center gap-4"
            data-spec="image+colors"
          >
            <RevIllustration
              alt=""
              data-test="product-card-image"
              :height="product.granularity === 'backbox' ? 224 : 128"
              :sizes="product.granularity === 'backbox' ? '224px' : '128px'"
              :src="product.imageSrc"
              :width="product.granularity === 'backbox' ? 224 : 128"
            />

            <ColorSwatches
              v-if="product.colorSwatches"
              :colors="product.colorSwatches"
              data-test="product-color-swatches"
            />
          </div>

          <div
            class="flex min-w-0 grow basis-[159px] flex-col items-start gap-6"
            data-spec="product-info"
          >
            <div class="flex flex-col gap-2" data-spec="titles">
              <span
                v-if="tags?.[0]"
                class="caption-bold md:absolute md:left-16 md:top-16"
                :style="{ color: tags[0].color_code }"
              >
                {{ tags[0].label }}
              </span>
              <h2>
                <RevButtonBase
                  ref="link"
                  class="after:focus-visible-outline-default-hi after:rounded-lg after:absolute after:inset-0 after:bg-transparent focus-visible:outline-none"
                  :to="product.url"
                  @click="emit('link-click')"
                >
                  <span
                    class="body-1-bold line-clamp-2"
                    data-test="product-title"
                  >
                    {{ product.title }}
                  </span>
                  <span
                    v-if="product.description"
                    class="text-static-default-low body-2"
                    :class="
                      options?.extendedDescription
                        ? 'line-clamp-3'
                        : 'line-clamp-2'
                    "
                    data-test="product-description"
                  >
                    {{ product.description }}
                  </span>
                </RevButtonBase>
              </h2>

              <span v-if="product.warranty" class="body-2">
                {{
                  i18n(translations.productCardWarranty, {
                    warranty: product.warranty,
                  })
                }}
                {{ i18n(translations.months) }}
              </span>

              <div
                v-if="product.granularity === 'backbox'"
                class="flex flex-wrap gap-4"
              >
                <ProductCardTag
                  v-if="product.grade"
                  :icon="isPremiumGrade ? IconDiamond : undefined"
                  :theme="isPremiumGrade ? 'dark' : undefined"
                >
                  {{ product.grade.name }}
                </ProductCardTag>
                <ProductCardTag
                  v-if="product.specialOfferType === BATTERY_SPECIAL_OFFER"
                >
                  {{ i18n(translations.specialOfferType7) }}
                </ProductCardTag>
              </div>
            </div>

            <ProductCardRatings
              v-if="
                product.reviewRating?.count && product.reviewRating?.average
              "
              :count="product.reviewRating.count"
              data-spec="rating"
              :score="product.reviewRating.average"
            />

            <div
              v-if="product.specifications && product.specifications.length > 0"
            >
              <ProductSpecs :specifications="product.specifications" />
            </div>
            <div v-if="product.price" data-spec="price-information">
              <div
                v-if="options?.withStartingFrom"
                class="text-static-default-low caption"
                data-test="product-starting-from"
              >
                {{ i18n(translations.startingFrom) }}
              </div>
              <ProductPrice
                :discounted-price="
                  product.discountedPrice && i18n.price(product.discountedPrice)
                "
                :price="i18n.price(product.price)"
                :price-new="product.priceNew && i18n.price(product.priceNew)"
              />
            </div>
          </div>
        </div>
      </div>
      <ProductCardDeal
        class="relative mt-8"
        :deal="product.deal"
        :granularity="product.granularity"
      />
    </div>

    <ProductComparisonCheckbox
      v-if="comparison?.isComparisonEnabled"
      class="absolute bottom-8 right-8 lg:bottom-auto lg:top-8"
      :is-checked="isProductCompared"
      :is-comparison-add-disabled="
        comparison.isComparisonAddProductDisabled ?? false
      "
      :is-responsive-comparison-enabled="
        comparison.isResponsiveComparisonEnabled ?? false
      "
      @change="emit('toggle-comparison-product', product.listingId)"
    />
    <div
      v-if="options?.withAddToCart || product.granularity === 'backbox'"
      class="mt-auto p-16 pt-0"
    >
      <AddToCartButton
        :id="product.listingId"
        :product-title="product.title"
        :text="i18n(translations.addToCart)"
        :trackingData="
          productToTrackingData(product.rawProduct, {
            list: options?.list,
            position: options?.position,
          })
        "
        @add-to-cart="addToCart"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { Grade, LinkInternal, Price } from '@backmarket/http-api'
import type { Deal } from '@backmarket/nuxt-layer-cms/products/algolia/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevIllustration } from '@ds/components/Illustration'
import { IconDiamond } from '@ds/icons/IconDiamond'
import type { Location } from '@ds/types/Location'

import { productToTrackingData } from '../../composables/useProductTracking'
import type {
  ComparisonProduct,
  Granularity,
  Product,
  Tag,
} from '../../models/product'
import AddToCartButton from '../AddToCartButton/AddToCartButton.vue'
import ColorSwatches from '../ColorSwatches.vue'
import ProductCardDeal from '../ProductCardDeal.vue'
import ProductCardRatings from '../ProductCardRatings.vue'
import ProductComparisonCheckbox from '../ProductComparison/components/ProductComparisonCheckbox.vue'
import ProductPrice from '../ProductPrice.vue'
import ProductSpecs from '../ProductSpecs.vue'

import {
  BATTERY_SPECIAL_OFFER,
  GRADE_PREMIUM_VALUE,
} from './ProductCard.constants'
import translations from './ProductCard.translations'
import ProductCardTag from './components/ProductCardTag.vue'

const { comparison, product } = defineProps<{
  product: {
    description?: string
    discountedPrice?: Price
    imageSrc: string
    listingId: string
    price: Price
    priceNew?: Price
    reviewRating?: {
      count: number
      average: number
    }
    title: string
    url: string | LinkInternal | Location
    grade?: Grade
    warranty?: number
    specifications?: Array<{
      label: string
      values: string[]
    }>
    colorSwatches?: Array<{ name: string; value?: string }>
    isComparisonEnabled?: boolean
    comparisonProducts?: ComparisonProduct[]
    isComparisonAddProductDisabled?: boolean
    isResponsiveComparisonEnabled?: boolean
    deal?: Deal
    rawProduct: Product
    granularity?: Granularity
    specialOfferType?: number
  }
  tags?: Tag[]
  options?: {
    withAddToCart?: boolean
    list?: string
    position?: number
    withStartingFrom?: boolean
    extendedDescription?: boolean
  }
  comparison?: {
    isComparisonEnabled?: boolean
    isComparisonAddProductDisabled?: boolean
    isResponsiveComparisonEnabled?: boolean
    comparisonProducts?: ComparisonProduct[]
  }
}>()

const emit = defineEmits([
  'add-to-cart',
  'toggle-comparison-product',
  'link-click',
])

const i18n = useI18n()

function addToCart({ status }: { status: string }) {
  emit('add-to-cart', { status })
}

const isPremiumGrade = computed(
  () => product.grade?.value === GRADE_PREMIUM_VALUE,
)

const isProductCompared = computed(() => {
  return (
    comparison?.comparisonProducts?.some(
      (p) => p.listingId === product.listingId,
    ) || false
  )
})
</script>
